import {ClassStep, School, SchoolClass, SchoolClassGroup} from "models"
import React, {useMemo, useRef} from "react"
import {ShapeComponent, shapeComponent} from "set-state-compare/build/shape-component"
import Button from "components/inputs/button"
import {hasFilterInQueryParams} from "components/user/unicorn/filter-changed"
import HayaSelect from "components/woof-haya-select"
import I18n from "shared/i18n"
import Input from "components/inputs/input"
import isUserA from "components/users/is-a"
import {loading} from "components/layout/loading-blocker"
import memo from "set-state-compare/build/memo"
import Params from "@kaspernj/api-maker/build/params"
import {Platform} from "react-native"
import PropTypes from "prop-types"
import propTypesExact from "prop-types-exact"
import useCurrentUser from "@kaspernj/api-maker/build/use-current-user"
import useDates from "components/utils/use-dates"
import useQueryParams from "on-location-changed/build/use-query-params"

const schoolClassGroupsEnabled = window.RAILS_ENV == "test"
const dateInputType = window.RAILS_ENV == "test" ? "text" : "date"

export default memo(shapeComponent(class ComponentsUserUnicornFilter extends ShapeComponent {
  static defaultProps = {
    classStepIds: null,
    dateFrom: null,
    dateTo: null,
    schoolClassIds: null,
    schoolClassGroupIds: null,
    schoolIds: null
  }

  static propTypes = propTypesExact({
    classStepIds: PropTypes.array,
    dateFrom: PropTypes.instanceOf(Date),
    dateTo: PropTypes.instanceOf(Date),
    schoolClassIds: PropTypes.array,
    schoolClassGroupIds: PropTypes.array,
    schoolIds: PropTypes.array
  })

  /** @returns {void} */
  setup() {
    const {l} = useDates()

    this.setInstance({l})
    this.currentUser = useCurrentUser()
    this.queryParams = useQueryParams()
    this.dateFromInputRef = useRef()
    this.dateToInputRef = useRef()

    this.useStates({
      dateFromInputValue: () => this.dateInputValue(this.p.dateFrom),
      dateToInputValue: () => this.dateInputValue(this.p.dateTo),
      schoolClassesChanges: undefined,
      defaultIds: this.loadDefaultIds(),
      classSteps: undefined,
      schools: undefined,
      schoolClasses: undefined,
      schoolClassGroups: undefined
    })

    this.dateFromText = this.formatDate(this.p.dateFrom)
    this.dateToText = this.formatDate(this.p.dateTo)
    this.dateFromTextTouched = false
    this.dateToTextTouched = false

    useMemo(() => {
      if (!this.currentUser) {
        return
      }

      this.loadClassSteps()
      this.loadSchools()
      this.loadSchoolClasses()

      if (schoolClassGroupsEnabled) {
        this.loadSchoolClassGroups()
      }
    }, [this.currentUser.id()])

    useMemo(() => {
      if (this.usesNativeDateInput()) {
        this.setState({
          dateFromInputValue: this.dateInputValue(this.p.dateFrom),
          dateToInputValue: this.dateInputValue(this.p.dateTo)
        })

        return
      }

      const dateFromText = this.dateInputValue(this.p.dateFrom)
      const dateToText = this.dateInputValue(this.p.dateTo)

      this.dateFromText = dateFromText
      this.dateToText = dateToText
      this.dateFromTextTouched = false
      this.dateToTextTouched = false

      if (Platform.OS == "web") {
        if (this.dateFromInputRef.current) this.dateFromInputRef.current.value = dateFromText
        if (this.dateToInputRef.current) this.dateToInputRef.current.value = dateToText
      }
    }, [this.tt.l, this.p.dateFrom, this.p.dateTo, this.queryParams.date_from, this.queryParams.date_to])
  }

  loadClassSteps = async () => {
    const loadingLabel = ClassStep.modelName().human({count: 2})

    await loading(loadingLabel, async () => {
      const classSteps = await ClassStep
        .ransack({s: "primary_school_step"})
        .select({ClassStep: ["id", "name"]})
        .toArray()

      this.setState({classSteps})
    })
  }

  loadDefaultIds = () => {
    const {classStepIds, schoolClassIds, schoolClassGroupIds, schoolIds} = this.p
    const defaultIds = [...classStepIds || [], ...schoolClassIds || [], ...schoolClassGroupIds || [], ...schoolIds || []]

    return defaultIds
  }

  loadSchools = async () => {
    const loadingLabel = School.modelName().human({count: 2})

    await loading(loadingLabel, async () => {
      const schools = await School
        .ransack({
          s: "current_translation_name",
          with_contact_id: this.tt.currentUser.contactId()
        })
        .select({School: ["hasProSubscription", "id", "name"]})
        .groupBy("id")
        .toArray()

      this.setState({schools})
    })
  }

  loadSchoolClasses = async () => {
    const loadingLabel = SchoolClass.modelName().human({count: 2})

    await loading(loadingLabel, async () => {
      const schoolClasses = await SchoolClass
        .ransack({
          s: "current_translation_name",
          with_contact_id: this.tt.currentUser.contactId()
        })
        .select({SchoolClass: ["hasProSubscription", "id", "interpretedName"]})
        .groupBy("id")
        .toArray()

      this.setState({schoolClasses})
    })
  }

  loadSchoolClassGroups = async () => {
    const loadingLabel = SchoolClassGroup.modelName().human({count: 2})

    await loading(loadingLabel, async () => {
      const schoolClassGroups = await SchoolClassGroup
        .ransack({s: "current_translation_name"})
        .select({SchoolClassGroup: ["id", "name"]})
        .toArray()

      this.setState({schoolClassGroups})
    })
  }

  /** @returns {React.ReactNode} */
  render() {
    return (
      <div className="components--user--unicorn--filter">
        <form onSubmit={this.tt.onSubmit}>
          <div style={{display: "flex", flexWrap: "wrap"}}>
            <div className="school-class-select-container">
              {this.s.schoolClasses && (!schoolClassGroupsEnabled || this.s.schoolClassGroups) && this.s.defaultIds !== undefined &&
                <HayaSelect
                  className="school-classes-select"
                  defaultValues={this.s.defaultIds}
                  id="school-classes-select"
                  multiple
                  onChange={this.tt.onSchoolClassesChanged}
                  options={this.schoolClassesOptions()}
                  woofInputStyling
                />
              }
            </div>
            <div className="date-from-container">
              <Input
                id="date_from"
                inputRef={this.dateFromInputRef}
                name="date_from"
                onChange={this.tt.onDateFromChanged}
                onClick={this.tt.onDateFromFocused}
                onFocus={this.tt.onDateFromFocused}
                style={{width: "100%"}}
                type={dateInputType}
                {...this.renderDateInputValueProps({date: this.p.dateFrom, value: this.s.dateFromInputValue})}
              />
            </div>
            <div className="date-to-container">
              <Input
                id="date_to"
                inputRef={this.dateToInputRef}
                name="date_to"
                onChange={this.tt.onDateToChanged}
                onClick={this.tt.onDateToFocused}
                onFocus={this.tt.onDateToFocused}
                style={{width: "100%"}}
                type={dateInputType}
                {...this.renderDateInputValueProps({date: this.p.dateTo, value: this.s.dateToInputValue})}
              />
            </div>
            <div className="search-submit-container">
              <Button className="search-submit" label={I18n.t("js.shared.search")} primary />
            </div>
          </div>
        </form>
      </div>
    )
  }

  /** @param {object} args */
  onDateFromChanged = (args) => {
    if (!this.usesNativeDateInput()) {
      this.dateFromText = this.inputChangeValue(args)
      this.dateFromTextTouched = true

      return
    }

    this.setState({dateFromInputValue: this.inputChangeValue(args)})
  }

  /** @param {object} args */
  onDateToChanged = (args) => {
    if (!this.usesNativeDateInput()) {
      this.dateToText = this.inputChangeValue(args)
      this.dateToTextTouched = true

      return
    }

    this.setState({dateToInputValue: this.inputChangeValue(args)})
  }

  onSchoolClassesChanged = ({options}) => {
    this.setState({schoolClassesChanges: options})
  }

  /** @param {Event} e */
  onSubmit = (e) => {
    e.preventDefault()

    const newParams = {}
    const dateFromValue = this.submitDateInputValue({
      ref: this.dateFromInputRef,
      stateValue: this.s.dateFromInputValue,
      textValue: this.dateFromText,
      touched: this.dateFromTextTouched
    })
    const dateToValue = this.submitDateInputValue({
      ref: this.dateToInputRef,
      stateValue: this.s.dateToInputValue,
      textValue: this.dateToText,
      touched: this.dateToTextTouched
    })
    const dateFrom = this.validDateText(dateFromValue)
    const dateTo = this.validDateText(dateToValue)

    // Get class steps, school classes, schools and school class groups
    let options

    if (this.s.schoolClassesChanges) {
      options = this.s.schoolClassesChanges
    } else if (!hasFilterInQueryParams(this.queryParams)) {
      // Insert defaults into params if this is the first filtering
      options = this.selectedOptionsFromDefaultIds()
    }

    if (options) {
      const classStepIds = options.filter((option) => option.model == "class-step").map((option) => option.value)
      const schoolIds = options.filter((option) => option.model == "school").map((option) => option.value)
      const schoolClassesIds = options.filter((option) => option.model == "school-class").map((option) => option.value)
      let schoolClassGroupsIds

      if (schoolClassGroupsEnabled) {
        schoolClassGroupsIds = options.filter((option) => option.model == "school-class-group").map((option) => option.value)
      }

      this.setState({schoolClassesChanges: undefined})

      newParams.class_steps = classStepIds
      newParams.schools = schoolIds
      newParams.school_classes = schoolClassesIds

      if (schoolClassGroupsEnabled) {
        newParams.school_class_groups = schoolClassGroupsIds
      }
    }

    // Date filters
    if (dateFrom) {
      newParams.date_from = I18n.strftime("%Y-%m-%d", new Date(dateFrom))
    } else {
      newParams.date_from = undefined
    }

    if (dateTo) {
      newParams.date_to = I18n.strftime("%Y-%m-%d", new Date(dateTo))
    } else {
      newParams.date_to = undefined
    }

    if (Object.keys(newParams).length > 0) {
      Params.changeParams(newParams)
    }
  }

  selectedOptionsFromDefaultIds = () => {
    const options = []

    for (const defaultId of this.s.defaultIds) {
      const classStep = this.s.classSteps.find((classStep) => classStep.id() == defaultId)
      const school = this.s.schools.find((school) => school.id() == defaultId)
      const schoolClass = this.s.schoolClasses.find((schoolClass) => schoolClass.id() == defaultId)
      let schoolClassGroup

      if (schoolClassGroupsEnabled) {
        schoolClassGroup = this.s.schoolClassGroups.find((schoolClassGroup) => schoolClassGroup.id() == defaultId)
      }

      let model

      if (classStep) {
        model = "class-step"
      } else if (school) {
        model = "school"
      } else if (schoolClass) {
        model = "school-class"
      } else if (schoolClassGroup) {
        model = "school-class-group"
      }

      if (model) options.push({model, value: defaultId})
    }

    return options
  }

  formatDate(date) {
    return date ? this.tt.l(date, "date") : ""
  }

  /**
   * @param {Date | null | undefined} date
   * @returns {string}
   */
  dateInputValue(date) {
    if (!date) {
      return ""
    }

    if (dateInputType == "date") {
      return I18n.strftime("%Y-%m-%d", date)
    }

    return this.formatDate(date)
  }

  /**
   * @param {object} args
   * @param {Date | null | undefined} args.date
   * @param {string} args.value
   * @returns {object}
   */
  renderDateInputValueProps({date, value}) {
    if (this.usesNativeDateInput()) {
      return {value}
    }

    return {defaultValue: this.dateInputValue(date)}
  }

  /**
   * @param {string} value
   * @returns {Date|null}
   */
  validDateText(value) {
    if (!value) return null

    const cleanedValue = value.trim()
    const isoMatch = cleanedValue.match(/^(\d{4})-(\d{2})-(\d{2})$/)

    if (isoMatch) {
      return this.dateFromNumbers({
        day: Number(isoMatch[3]),
        month: Number(isoMatch[2]),
        year: Number(isoMatch[1])
      })
    }

    const shortDayFirstMatch = cleanedValue.match(/^(\d{2})-(\d{2})-(\d{2})$/)

    if (shortDayFirstMatch) {
      return this.dateFromNumbers({
        day: Number(shortDayFirstMatch[1]),
        month: Number(shortDayFirstMatch[2]),
        year: 2000 + Number(shortDayFirstMatch[3])
      })
    }

    const longDayFirstMatch = cleanedValue.match(/^(\d{2})-(\d{2})-(\d{4})$/)

    if (longDayFirstMatch) {
      return this.dateFromNumbers({
        day: Number(longDayFirstMatch[1]),
        month: Number(longDayFirstMatch[2]),
        year: Number(longDayFirstMatch[3])
      })
    }

    const parsedDate = new Date(cleanedValue)

    if (Number.isNaN(parsedDate.getTime())) {
      return null
    }

    return parsedDate
  }

  /**
   * @param {object} args
   * @param {number} args.day
   * @param {number} args.month
   * @param {number} args.year
   * @returns {Date|null}
   */
  dateFromNumbers({day, month, year}) {
    const parsedDate = new Date(year, month - 1, day)

    if (
      parsedDate.getFullYear() != year ||
      parsedDate.getMonth() != month - 1 ||
      parsedDate.getDate() != day
    ) {
      return null
    }

    return parsedDate
  }

  /**
   * @param {object} args
   * @returns {string}
   */
  inputChangeValue(args) {
    const value = args?.target?.value ?? args?.currentTarget?.value ?? args?.nativeEvent?.text ?? args?.value ?? ""

    if (typeof value == "string") {
      return value
    }

    return ""
  }

  /**
   * @param {object} args
   * @param {React.RefObject<HTMLInputElement>} args.ref
   * @param {string} args.stateValue
   * @param {string} args.textValue
   * @param {boolean} args.touched
   * @returns {string}
   */
  submitDateInputValue({ref, stateValue, textValue, touched}) {
    if (this.usesNativeDateInput()) {
      return stateValue
    }

    if (touched) {
      return textValue
    }

    return ref.current?.value || ""
  }

  /** @returns {void} */
  onDateFromFocused = () => {
    this.openDatePicker({ref: this.dateFromInputRef})
  }

  /** @returns {void} */
  onDateToFocused = () => {
    this.openDatePicker({ref: this.dateToInputRef})
  }

  /**
   * @param {object} args
   * @param {React.RefObject<HTMLInputElement>} args.ref
   * @returns {void}
   */
  openDatePicker({ref}) {
    if (Platform.OS != "web" || !this.usesNativeDateInput()) {
      return
    }

    if (typeof ref.current?.showPicker == "function") {
      ref.current.showPicker()
    }
  }

  /** @returns {boolean} */
  usesNativeDateInput() {
    return dateInputType == "date"
  }

  schoolClassesOptions = () => {
    const options = []

    options.push({
      key: "schools-group",
      text: School.modelName().human({count: 2}),
      type: "group"
    })

    if (this.s.schools) {
      for (const school of this.s.schools) {
        options.push({
          disabled: !isUserA(this.currentUser, "admin") && !school.hasProSubscription(),
          model: "school",
          text: school.name(),
          value: school.id()
        })
      }
    }

    options.push({
      key: "class-steps-group",
      text: ClassStep.modelName().human({count: 2}),
      type: "group"
    })

    for (const classStep of this.s.classSteps) {
      options.push({
        model: "class-step",
        text: classStep.name(),
        value: classStep.id()
      })
    }

    if (schoolClassGroupsEnabled) {
      options.push({
        key: "school-class-groups",
        text: SchoolClassGroup.modelName().human({count: 2}),
        type: "group"
      })

      if (this.s.schoolClassGroups) {
        options.push(...this.s.schoolClassGroups.map((schoolClassGroup) => ({
          model: "school-class-group",
          text: schoolClassGroup.name(),
          value: schoolClassGroup.id()
        })))
      }
    }

    options.push({
      key: "school-classes",
      text: SchoolClass.modelName().human({count: 2}),
      type: "group"
    })

    if (this.s.schoolClasses) {
      options.push(...this.s.schoolClasses.map((schoolClass) => ({
        disabled: !isUserA(this.currentUser, "admin") && !schoolClass.hasProSubscription(),
        model: "school-class",
        text: schoolClass.interpretedName(),
        value: schoolClass.id()
      })))
    }

    return options
  }
}))
